import { TABLE_CHAT_NOT_A_MEMBER } from "@bing/shared";
import { isChannelRefusal } from "./channel-refusal";
import { reportClientFault } from "./client-report";
import { lifecycle } from "./lifecycle";

/**
 * THE INITIAL-JOIN BEACON — the sibling of `boundedReacquire`, for the one connection attempt
 * the lifecycle has never been able to see.
 *
 * WHY THIS EXISTS (2026-08-22, user 7488550206). She accepted a friend's invite; `joinById` was
 * granted its seat reservation over HTTP and the WebSocket upgrade then arrived 15–20s later —
 * past Colyseus's 15s reservation TTL — or never at all. The server refused it («seat reservation
 * expired», the 34-byte `101` in the origin log) and her screen simply sat on «در حال اتصال…».
 * THREE instruments recorded that event and not one of them was ours: her friends were seated in
 * the very table she could not enter, and we learned about it because she wrote in.
 *
 * The gap is structural: `manageRoom` is handed an ALREADY-CONNECTED room, so the whole reconnect
 * apparatus — the backoff cycle, the `reconnect-gave-up` beacon — begins one step too late. A
 * first join that rejects, or that never settles at all, reported nothing.
 *
 * Three rules keep this safe to leave on permanently:
 *  - it is a PASS-THROUGH. The caller gets the original promise, so the resolved room and the
 *    thrown error are byte-identical to today and a hung join still hangs exactly as it did.
 *    This watches; it does not rescue. Bounding the join is a deliberate UX change, not this.
 *  - it reports at most ONE beacon per (connection, outcome) per session. Four failing joins in
 *    four minutes is one fact about a network, not four rows.
 *  - the reason is a CLOSED VOCABULARY, never the raw message — see {@link joinFailureReason}.
 */

/**
 * When a pending join stops being "slow" and becomes evidence.
 *
 * Not a taste: 15s WAS Colyseus's seat-reservation TTL (`COLYSEUS_SEAT_RESERVATION_TIME`, unset ⇒
 * the 15s default — verified in the container), so an upgrade that had not landed by then was
 * refused. That is precisely what her `101 34`s were.
 *
 * ⚠️ THE WINDOW IS 30 s SINCE 2026-09-23 (`infra/docker-compose.yml`) AND THIS DELIBERATELY STAYS
 * 15: it is the line every `stalled-*` beacon has been counted against since 2026-08-22, and moving
 * it would change what the series means on the day the thing it measures changed. Read it now as
 * «slow», no longer as «doomed»: a join landing at 15–30 s is admitted and files
 * `stalled-recovered`, where it used to be refused and file `stalled-refused`.
 */
export const JOIN_STALL_MS = 15_000;

/**
 * How long a stalled join is WATCHED before the verdict is filed.
 *
 * A 15s snapshot cannot tell a slow path from a dead one, and shipping a beacon that conflates
 * them is how a signal becomes noise — the lesson `style-broken` already paid for twice (its
 * `RECHECK_MS`, then its `outcome=`). On an Iranian mobile path a join that completes at +18s is
 * ordinary; one still pending at +35s is the thing we are hunting.
 */
export const JOIN_WATCH_MS = 20_000;

/** Longest unrecognised message kept on the beacon (the only branch that may vary a fingerprint). */
const MAX_ERR_CHARS = 80;

export interface JoinWatchOptions {
  /** Connection name, matching the lifecycle's (`online:hokm-online`, `presence`, `dm`, `chat`,
   *  `solo:hokm`). Emitted FIRST so the api's existing `^conn=` anchors keep working. */
  conn: string;
  /** Which join path this was: `quick` | `join` | `create` | `rejoin` | `resume` | `solo` |
   *  `presence` | `dm` | `chat`. An invite that cannot be entered and a quick-match that cannot
   *  find a table are different populations. */
  kind: string;
  /** The app's fused activity signal; defaults to the lifecycle's own. Injected in tests. */
  isActive?: () => boolean;
}

type Outcome = "refused" | "stalled-refused" | "stalled-recovered" | "stalled-never" | "left";

/** One report per (connection, outcome) per session — see the header. */
const reported = new Set<string>();

/**
 * Watch one INITIAL join. Returns the very promise it was given.
 */
export function watchJoin<R>(opts: JoinWatchOptions, join: Promise<R>): Promise<R> {
  const startedAt = Date.now();
  const isActive = opts.isActive ?? ((): boolean => lifecycle.appActive);

  /**
   * A POCKETED PHONE IS NOT A BROKEN JOIN. A locked screen suspends the WebView, so a join that
   * spans one legitimately stalls; without this field those would swamp the population this
   * beacon exists to measure. Sampled at the start, at the stall deadline and at the verdict —
   * a discriminator, not an accountant: a lock that both begins and ends between two samples is
   * not caught, which is the honest limit of three samples and enough to triage on.
   */
  let sawActive = false;
  let sawInactive = false;
  const sample = (): void => {
    try {
      if (isActive()) sawActive = true;
      else sawInactive = true;
    } catch {
      // a telemetry probe must never be the thing that throws
    }
  };
  sample();

  let settled = false;
  let stalled = false;
  /** ONE beacon per join attempt. Without this, a join that we already filed `stalled-never`
   *  for and which then lands twenty seconds later files `stalled-recovered` on top of it —
   *  two rows describing one attempt, and the verdict contradicted by its own late arrival. */
  let filed = false;

  const stallTimer = setTimeout(() => {
    stalled = true;
    sample();
  }, JOIN_STALL_MS);

  const verdictTimer = setTimeout(() => {
    if (settled) return;
    report("stalled-never");
  }, JOIN_STALL_MS + JOIN_WATCH_MS);

  /**
   * THE PAGE GOING AWAY MID-JOIN — the behaviour that started this whole thread.
   *
   * She re-opened the app while joins were pending (17:51:01 → :19 → :42 on 2026-08-22), and each
   * reload took the pending watch down with its own document, so the verdict above never landed.
   * Without this, the population the beacon exists for is precisely the population it misses. The
   * report rides `keepalive`, which is what lets it survive the teardown that triggered it.
   *
   * `left` means «we STOPPED BEING ABLE TO WATCH», not «they gave up» — on iOS WebKit fires
   * `pagehide` liberally, including for a backgrounding the page may well return from. That
   * ambiguity is exactly why it is its own outcome and not folded into `stalled-never`.
   *
   * GATED ON THE JOIN ALREADY BEING STALLED. Closing the app two seconds into a join is somebody
   * changing their mind; below that line we genuinely cannot tell that from a failure, and
   * reporting it anyway would bury the signal under every ordinary app close.
   */
  const onPageHide = (): void => {
    if (settled || !stalled) return;
    report("left");
  };
  try {
    window.addEventListener("pagehide", onPageHide);
  } catch {
    // no window (a non-browser host) — the timers still cover every other outcome
  }

  function report(outcome: Outcome, error?: unknown): void {
    if (filed) return;
    filed = true;
    // A join we have already spoken about needs no further watching — and a listener left
    // attached would outlive every join of the session.
    try {
      window.removeEventListener("pagehide", onPageHide);
    } catch {
      // symmetric with the guarded add above
    }
    const key = `${opts.conn}|${outcome}`;
    if (reported.has(key)) return;
    reported.add(key);
    sample();
    const reason = error === undefined ? undefined : joinFailureReason(error);
    reportClientFault("join-failed", {
      conn: opts.conn,
      kind: opts.kind,
      outcome,
      elapsedMs: Date.now() - startedAt,
      active: sawActive && sawInactive ? "mixed" : sawInactive ? "no" : "yes",
      // LAST, and only when we have nothing better: the sole field that can vary a fingerprint.
      ...(reason === undefined ? {} : { reason }),
      ...(reason === "other" ? { err: sanitize(error) } : {}),
    });
  }

  const done = (): void => {
    settled = true;
    clearTimeout(stallTimer);
    clearTimeout(verdictTimer);
    try {
      window.removeEventListener("pagehide", onPageHide);
    } catch {
      // symmetric with the guarded add above
    }
  };

  void join.then(
    () => {
      done();
      if (stalled) report("stalled-recovered");
    },
    (error: unknown) => {
      done();
      report(stalled ? "stalled-refused" : "refused", error);
    },
  );

  return join;
}

/**
 * The refusal, as one of a CLOSED set of slugs.
 *
 * `fingerprintOf` folds digits to `#` but leaves `B2JIDbNDU` alone, so passing Colyseus's raw
 * message through would mint one ErrorLog row per table — 1,299 `joinById` attempts a day, each
 * its own row, which is a flood dressed up as telemetry. It also keeps room ids off the wire.
 *
 * The slugs are the populations worth counting separately:
 *  - `locked` / `full`  — the table filled or started first (the player gets a correct sentence);
 *  - `not-found`        — the table is gone;
 *  - `seat-expired`     — THE HASTI SHAPE: the reservation was minted and the upgrade came too late;
 *  - `policy`           — a Persian server refusal (coming-soon, banned, the concurrent-game cap);
 *  - `other`            — anything else, and the only branch that carries a message.
 */
export function joinFailureReason(error: unknown): string {
  // The channel refusal is `policy`, as its bare form always filed — BEFORE the patterns below, which
  // would otherwise read its admin-written channel titles («… is locked») as a room's fate.
  if (isChannelRefusal(error)) return "policy";
  const message = messageOf(error);
  if (/is locked/i.test(message)) return "locked";
  if (/already full/i.test(message)) return "full";
  // OUR OWN English refusal from `online-controller.ts` — «already full» is Colyseus's wording,
  // not ours, so this filed as `other` (32 events / 30 players in 24 h).
  if (/table is full/i.test(message)) return "full";
  if (/not found/i.test(message)) return "not-found";
  // Colyseus's SECOND way of saying a room is gone. It was the single biggest `other` on prod —
  // 238 events / ~186 players in 24 h — so the histogram could not show what was actually
  // happening. Kept in step with `roomFailure` in `join-error.ts`, which decides the WORDING;
  // this decides what the beacon is able to tell us afterwards.
  if (/has been disposed/i.test(message)) return "not-found";
  /*
   * THE TABLE-CHAT REFUSAL — a DIFFERENT population from `not-found`, and kept apart deliberately.
   * `not-found` is the GAME room being gone, which is a player tapping a dead invite; this is the
   * CHAT room refusing because the table it belonged to has disposed, which happens to a player
   * who was sitting at it. Filed as `other` with the raw English on it until 2026-09-22 — 12
   * groups in 6 days on the current bundle, enough to hide something real behind.
   * ⚠️ MATCHED ON THE SHARED CONSTANT, not a copy of the sentence: `TABLE_CHAT_NOT_A_MEMBER` is
   * a cross-service contract and `deploy-safety.test.ts` pins the game-server literal to it.
   */
  if (message.includes(TABLE_CHAT_NOT_A_MEMBER)) return "table-gone";
  if (/seat reservation expired/i.test(message)) return "seat-expired";
  if (/reconnection token invalid or expired/i.test(message)) return "seat-expired";
  // Our own server-sent refusals are Persian; every transport/system string is ASCII.
  if (/[؀-ۿ]/.test(message)) return "policy";
  return "other";
}

/** An unrecognised message, short and with its ids removed (see {@link joinFailureReason}). */
function sanitize(error: unknown): string {
  return messageOf(error)
    .replace(/"[^"]*"/g, '"…"')
    .replace(/\b(?=[\dA-Za-z_-]*\d)(?=[\dA-Za-z_-]*[A-Za-z])[\dA-Za-z_-]{6,}\b/g, "…")
    .slice(0, MAX_ERR_CHARS);
}

function messageOf(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return String(error ?? "");
}

/** Test seam — the latch is module state, deliberately per-session. */
export function __resetJoinWatchForTests(): void {
  reported.clear();
}
